import { useState, useCallback, useMemo } from 'react';
import { Controller, useWatch, type Control, type FieldValues, type Path } from 'react-hook-form';
import { type Key } from 'react-aria-components';
import { ComboBox, type ComboBoxProps } from './ComboBox';

interface ComboBoxItem {
  id: string;
  label: string;
}

interface FormComboBoxProps<TForm extends FieldValues, TItem extends ComboBoxItem> extends Omit<
  ComboBoxProps<TItem>,
  'selectedKey' | 'onSelectionChange' | 'inputValue' | 'onInputChange' | 'errorMessage'
> {
  control: Control<TForm>;
  name: Path<TForm>;
}

export function FormComboBox<TForm extends FieldValues, TItem extends ComboBoxItem>({
  control,
  name,
  items,
  ...props
}: FormComboBoxProps<TForm, TItem>) {
  const [inputValue, setInputValue] = useState('');

  const fieldValue = useWatch({ control, name }) as Key | null;

  const [prevFieldValue, setPrevFieldValue] = useState(fieldValue);
  if (fieldValue !== prevFieldValue) {
    setPrevFieldValue(fieldValue);
    setInputValue('');
  }

  const getLabel = useCallback(
    (key: Key | null) => {
      if (key == null) return '';
      const item = items.find((i) => i.id === String(key));
      return item?.label ?? '';
    },
    [items]
  );

  const selectedLabel = getLabel(fieldValue);
  const displayValue = inputValue || selectedLabel;

  const filteredItems = useMemo(() => {
    if (!inputValue || inputValue === selectedLabel) return items;
    const lower = inputValue.toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(lower));
  }, [inputValue, selectedLabel, items]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <ComboBox
          {...props}
          items={filteredItems}
          selectedKey={(field.value as Key) ?? null}
          inputValue={displayValue}
          onInputChange={setInputValue}
          onSelectionChange={(key) => {
            field.onChange(key);
            setInputValue(key ? getLabel(key) : '');
            field.onBlur();
          }}
          errorMessage={fieldState.error?.message}
        />
      )}
    />
  );
}
